import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { Download, BarChart3, RefreshCw, FileText, Upload, AlertTriangle, CheckCircle, Printer } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getShiftDate, getShiftMonthStart } from '../utils/shiftDate';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt = (n) => (n == null || n === '' ? '—' : typeof n === 'number' ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n);
const fmtQ = (n) => (n == null ? '—' : n);

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const FY_MONTHS = ['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar'];

function exportToExcel(data, filename, headers) {
  const ws = XLSX.utils.json_to_sheet(data, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename + '.xlsx');
}

function ReportHeader({ title, subtitle, onRefresh, loading, onExport, onPdf }) {
  return (
    <div className="flex items-start justify-between mb-4">
      <div>
        <h2 className="text-lg font-bold text-slate-800">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        {onPdf && (
          <Button size="sm" variant="outline" className="border-slate-400 text-slate-700 hover:bg-slate-100" onClick={onPdf}>
            <Printer className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        )}
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={onExport}>
          <Download className="h-4 w-4 mr-1.5" /> Export Excel
        </Button>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-white border rounded-lg p-4 text-center">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-slate-800 mt-1">{value}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 1. Daily Material Issued Register
// ─────────────────────────────────────────────────────────────
function DailyIssuedReport() {
  const today = getShiftDate();
  const firstOfMonth = getShiftMonthStart();
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [vehicleFilter, setVehicleFilter] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/reports/daily-issued`, {
        headers: authHeaders(),
        params: { date_from: dateFrom, date_to: dateTo }
      });
      setData(res.data.data || []);
      setTotal(res.data.total_amount || 0);
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  const vehicles = [...new Set(data.map(r => r.door_no).filter(Boolean))].sort();
  const filtered = vehicleFilter ? data.filter(r => r.door_no === vehicleFilter) : data;
  const filteredTotal = filtered.reduce((s, r) => s + (r.amount || 0), 0);

  const exportXls = () => {
    const rows = filtered.map((r, i) => ({
      'SL': i + 1, 'Date': r.date, 'Vehicle No': r.door_no,
      'Part No': r.part_no, 'Part Name': r.part_name, 'Make': r.make,
      'Category': r.category, 'UOM': r.uom, 'Qty': r.qty,
      'Unit Price': r.unit_price, 'Amount': r.amount, 'Notes': r.notes
    }));
    exportToExcel(rows, `Daily_Material_Issued_${dateFrom}_to_${dateTo}`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(13);
    doc.text(`Daily Material Issued Register — ${dateFrom} to ${dateTo}${vehicleFilter ? ` | Vehicle: ${vehicleFilter}` : ''}`, 14, 15);
    doc.autoTable({
      startY: 22,
      head: [['SL', 'Date', 'Vehicle No', 'Part No', 'Part Name', 'Make', 'Category', 'Qty', 'Unit Price', 'Amount']],
      body: filtered.map((r, i) => [i+1, r.date, r.door_no, r.part_no||'', r.part_name, r.make||'', r.category, r.qty, `₹${r.unit_price}`, `₹${r.amount}`]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 41, 59] },
      foot: [['', '', '', '', '', '', '', '', 'Grand Total', `₹${filteredTotal.toFixed(2)}`]],
      footStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: 'bold' },
    });
    doc.save(`Daily_Issued_${dateFrom}_to_${dateTo}.pdf`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end bg-slate-50 rounded-lg p-4">
        <div><Label>From Date</Label><Input type="date" className="mt-1 w-40" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
        <div><Label>To Date</Label><Input type="date" className="mt-1 w-40" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
        <div>
          <Label>Vehicle Filter</Label>
          <select className="mt-1 h-9 border rounded-md px-2 text-sm bg-white min-w-[140px]" value={vehicleFilter} onChange={e => setVehicleFilter(e.target.value)} data-testid="issued-vehicle-filter">
            <option value="">All Vehicles</option>
            {vehicles.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <Button onClick={fetch} disabled={loading} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="issued-fetch-btn">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Generate Report
        </Button>
      </div>
      <ReportHeader title="Daily Material Issued Register" subtitle={`${dateFrom} to ${dateTo}`} onRefresh={fetch} loading={loading} onExport={exportXls} onPdf={exportPdf} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <SummaryCard label="Total Records" value={filtered.length} />
        <SummaryCard label="Total Amount" value={`₹ ${filteredTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader className="bg-slate-800 text-white">
            <TableRow>
              {['SL','Date','Vehicle No','Part No','Part Name','Make','Category','UOM','Qty','Unit Price','Amount'].map(h => (
                <TableHead key={h} className="text-white text-xs py-2 px-3">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">No data — click Generate Report</TableCell></TableRow>
            ) : filtered.map((r, i) => (
              <TableRow key={i} className="hover:bg-slate-50 text-sm">
                <TableCell className="px-3 py-1.5 text-slate-500">{i + 1}</TableCell>
                <TableCell className="px-3 py-1.5">{r.date}</TableCell>
                <TableCell className="px-3 py-1.5 font-medium">{r.door_no}</TableCell>
                <TableCell className="px-3 py-1.5 text-slate-600">{r.part_no || '—'}</TableCell>
                <TableCell className="px-3 py-1.5">{r.part_name}</TableCell>
                <TableCell className="px-3 py-1.5 text-slate-600">{r.make || '—'}</TableCell>
                <TableCell className="px-3 py-1.5"><Badge variant="outline" className="text-xs">{r.category}</Badge></TableCell>
                <TableCell className="px-3 py-1.5">{r.uom}</TableCell>
                <TableCell className="px-3 py-1.5 text-center font-medium">{r.qty}</TableCell>
                <TableCell className="px-3 py-1.5 text-right">₹{fmt(r.unit_price)}</TableCell>
                <TableCell className="px-3 py-1.5 text-right font-semibold text-slate-800">₹{fmt(r.amount)}</TableCell>
              </TableRow>
            ))}
            {filtered.length > 0 && (
              <TableRow className="bg-slate-100 font-bold">
                <TableCell colSpan={10} className="px-3 py-2 text-right">Grand Total</TableCell>
                <TableCell className="px-3 py-2 text-right">₹{fmt(filteredTotal)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 2. Daily Material Received Register
// ─────────────────────────────────────────────────────────────
function DailyReceivedReport() {
  const today = getShiftDate();
  const firstOfMonth = getShiftMonthStart();
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/reports/daily-received`, {
        headers: authHeaders(),
        params: { date_from: dateFrom, date_to: dateTo }
      });
      setData(res.data.data || []);
      setTotal(res.data.total_amount || 0);
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  const exportXls = () => {
    const rows = data.map((r, i) => ({
      'SL': i+1,'Date': r.date,'Supplier': r.supplier,'PO No': r.po_no,
      'Make': r.make,'Used For': r.used_for,'Part No': r.part_no,
      'Part Name': r.part_name,'UOM': r.uom,'Qty': r.qty,
      'Unit Price': r.unit_price,'Amount': r.amount,'Bill No': r.bill_no,'GRN No': r.grn_no
    }));
    exportToExcel(rows, `Daily_Material_Received_${dateFrom}_to_${dateTo}`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(12);
    doc.text(`Daily Material Received Register — ${dateFrom} to ${dateTo}`, 14, 15);
    doc.autoTable({
      startY: 22,
      head: [['SL', 'Date', 'Supplier', 'PO No', 'Part Name', 'Qty', 'Unit Price', 'Amount', 'GRN No']],
      body: data.map((r, i) => [i+1, r.date, r.supplier, r.po_no||'', r.part_name, r.qty, `₹${r.unit_price}`, `₹${r.amount}`, r.grn_no||'']),
      styles: { fontSize: 8 }, headStyles: { fillColor: [30, 41, 59] },
    });
    doc.save(`Daily_Received_${dateFrom}_to_${dateTo}.pdf`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end bg-slate-50 rounded-lg p-4">
        <div><Label>From Date</Label><Input type="date" className="mt-1 w-40" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
        <div><Label>To Date</Label><Input type="date" className="mt-1 w-40" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
        <Button onClick={fetch} disabled={loading} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="received-fetch-btn">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Generate Report
        </Button>
      </div>
      <ReportHeader title="Daily Material Received Register" subtitle={`${dateFrom} to ${dateTo}`} onRefresh={fetch} loading={loading} onExport={exportXls} onPdf={exportPdf} />
      <div className="grid grid-cols-2 gap-3 mb-4">
        <SummaryCard label="Total Records" value={data.length} />
        <SummaryCard label="Total Amount" value={`₹ ${total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader className="bg-slate-800">
            <TableRow>
              {['SL','Date','Supplier','PO No','Make','Used For','Part No','Part Name','UOM','Qty','Unit Price','Amount','Bill No','GRN No'].map(h => (
                <TableHead key={h} className="text-white text-xs py-2 px-3">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={14} className="text-center text-slate-400 py-8">No data — click Generate Report</TableCell></TableRow>
            ) : data.map((r, i) => (
              <TableRow key={i} className="hover:bg-slate-50 text-sm">
                <TableCell className="px-3 py-1.5 text-slate-500">{i+1}</TableCell>
                <TableCell className="px-3 py-1.5">{r.date}</TableCell>
                <TableCell className="px-3 py-1.5 font-medium">{r.supplier || '—'}</TableCell>
                <TableCell className="px-3 py-1.5 text-slate-600">{r.po_no || '—'}</TableCell>
                <TableCell className="px-3 py-1.5">{r.make || '—'}</TableCell>
                <TableCell className="px-3 py-1.5">{r.used_for || '—'}</TableCell>
                <TableCell className="px-3 py-1.5 text-slate-600">{r.part_no || '—'}</TableCell>
                <TableCell className="px-3 py-1.5">{r.part_name}</TableCell>
                <TableCell className="px-3 py-1.5">{r.uom}</TableCell>
                <TableCell className="px-3 py-1.5 text-center">{r.qty}</TableCell>
                <TableCell className="px-3 py-1.5 text-right">₹{fmt(r.unit_price)}</TableCell>
                <TableCell className="px-3 py-1.5 text-right font-semibold">₹{fmt(r.amount)}</TableCell>
                <TableCell className="px-3 py-1.5 text-slate-600">{r.bill_no || '—'}</TableCell>
                <TableCell className="px-3 py-1.5 text-slate-600">{r.grn_no || '—'}</TableCell>
              </TableRow>
            ))}
            {data.length > 0 && (
              <TableRow className="bg-slate-100 font-bold">
                <TableCell colSpan={11} className="px-3 py-2 text-right">Grand Total</TableCell>
                <TableCell className="px-3 py-2 text-right">₹{fmt(total)}</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 3. Opening Stock Report
// ─────────────────────────────────────────────────────────────
function OpeningStockReport() {
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/reports/opening-stock`, {
        headers: authHeaders(),
        params: { month: parseInt(month), year: parseInt(year) }
      });
      setData(res.data.data || []);
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  }, [month, year]);

  const monthName = MONTHS_SHORT[parseInt(month) - 1];
  const exportXls = () => {
    const rows = data.map((r, i) => ({ 'SL': i+1, 'Part Name': r.part_name, 'Part No': r.part_no, 'Make': r.make, 'Category': r.category, 'UOM': r.uom, 'Opening Stock': r.opening_stock, 'Received': r.received, 'Total Stock': r.total_stock, 'Unit Price': r.unit_price, 'Amount': r.amount, 'Total Consumed': r.total_consumed, 'Closing Stock': r.closing_stock, 'Closing Amount': r.closing_amount }));
    exportToExcel(rows, `Opening_Stock_${monthName}_${year}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end bg-slate-50 rounded-lg p-4">
        <div>
          <Label>Month</Label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="mt-1 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS_SHORT.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Year</Label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="mt-1 w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024,2025,2026,2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={fetch} disabled={loading} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="stock-fetch-btn">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Generate Report
        </Button>
      </div>
      <ReportHeader title="Opening Stock Report" subtitle={`${monthName} ${year}`} onRefresh={fetch} loading={loading} onExport={exportXls} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <SummaryCard label="Total Parts" value={data.length} />
        <SummaryCard label="Total Stock Value" value={`₹ ${data.reduce((s,r) => s + r.amount, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
        <SummaryCard label="Total Consumed" value={data.reduce((s,r) => s + r.total_consumed, 0)} />
        <SummaryCard label="Closing Value" value={`₹ ${data.reduce((s,r) => s + r.closing_amount, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader className="bg-slate-800">
            <TableRow>
              {['SL','Part Name','Part No','Make','Category','UOM','Opening Stock','Received','Total Stock','Unit Price','Amount','Total Consumed','Closing Stock','Closing Amount'].map(h => (
                <TableHead key={h} className="text-white text-xs py-2 px-2">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={14} className="text-center text-slate-400 py-8">No data — click Generate Report</TableCell></TableRow>
            ) : data.map((r, i) => (
              <TableRow key={i} className="hover:bg-slate-50 text-sm">
                <TableCell className="px-2 py-1.5 text-slate-500">{i+1}</TableCell>
                <TableCell className="px-2 py-1.5 font-medium max-w-[200px] truncate">{r.part_name}</TableCell>
                <TableCell className="px-2 py-1.5 text-slate-600">{r.part_no || '—'}</TableCell>
                <TableCell className="px-2 py-1.5">{r.make || '—'}</TableCell>
                <TableCell className="px-2 py-1.5"><Badge variant="outline" className="text-xs">{r.category}</Badge></TableCell>
                <TableCell className="px-2 py-1.5">{r.uom}</TableCell>
                <TableCell className="px-2 py-1.5 text-center">{fmtQ(r.opening_stock)}</TableCell>
                <TableCell className="px-2 py-1.5 text-center text-emerald-700 font-medium">{fmtQ(r.received)}</TableCell>
                <TableCell className="px-2 py-1.5 text-center font-semibold">{fmtQ(r.total_stock)}</TableCell>
                <TableCell className="px-2 py-1.5 text-right">₹{fmt(r.unit_price)}</TableCell>
                <TableCell className="px-2 py-1.5 text-right">₹{fmt(r.amount)}</TableCell>
                <TableCell className="px-2 py-1.5 text-center text-red-600">{fmtQ(r.total_consumed)}</TableCell>
                <TableCell className="px-2 py-1.5 text-center font-semibold">{fmtQ(r.closing_stock)}</TableCell>
                <TableCell className="px-2 py-1.5 text-right font-semibold">₹{fmt(r.closing_amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 4. Vehicle-wise Parts Issue Details
// ─────────────────────────────────────────────────────────────
function VehiclePartsIssuedReport() {
  const today = getShiftDate();
  const firstOfMonth = getShiftMonthStart();
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [vehicleFilter, setVehicleFilter] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/reports/vehicle-parts-issued`, {
        headers: authHeaders(),
        params: { date_from: dateFrom, date_to: dateTo }
      });
      setData(res.data.data || []);
      setTotal(res.data.total_amount || 0);
      // Auto-expand first vehicle
      const first = res.data.data?.[0]?.door_no;
      if (first) setExpanded({ [first]: true });
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  const toggle = (v) => setExpanded(prev => ({ ...prev, [v]: !prev[v] }));
  const filteredData = vehicleFilter ? data.filter(v => v.door_no === vehicleFilter) : data;
  const filteredTotal = filteredData.reduce((s, v) => s + v.subtotal, 0);

  const exportXls = () => {
    const rows = [];
    filteredData.forEach(veh => {
      veh.items.forEach((r, i) => rows.push({ 'Vehicle': veh.door_no, 'SL': i+1, 'Date': r.date, 'Part No': r.part_no, 'Part Name': r.part_name, 'Make': r.make, 'Category': r.category, 'UOM': r.uom, 'Qty': r.qty, 'Unit Price': r.unit_price, 'Amount': r.amount }));
      rows.push({ 'Vehicle': '', 'SL': '', 'Date': '', 'Part No': '', 'Part Name': `SUBTOTAL — ${veh.door_no}`, 'Make': '', 'Category': '', 'UOM': '', 'Qty': '', 'Unit Price': '', 'Amount': veh.subtotal });
    });
    exportToExcel(rows, `Vehicle_Parts_Issued_${dateFrom}_to_${dateTo}`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(12);
    doc.text(`Vehicle Parts Issued — ${dateFrom} to ${dateTo}${vehicleFilter ? ` | ${vehicleFilter}` : ''}`, 14, 15);
    const body = [];
    filteredData.forEach(veh => {
      veh.items.forEach((r, i) => body.push([veh.door_no, i+1, r.date, r.part_no||'', r.part_name, r.qty, `₹${r.unit_price}`, `₹${r.amount}`]));
      body.push(['', '', '', '', `SUBTOTAL — ${veh.door_no}`, '', '', `₹${veh.subtotal}`]);
    });
    doc.autoTable({ startY: 22, head: [['Vehicle','SL','Date','Part No','Part Name','Qty','Unit Price','Amount']], body, styles: { fontSize: 8 }, headStyles: { fillColor: [30, 41, 59] } });
    doc.save(`Vehicle_Parts_Issued_${dateFrom}_to_${dateTo}.pdf`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end bg-slate-50 rounded-lg p-4">
        <div><Label>From Date</Label><Input type="date" className="mt-1 w-40" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
        <div><Label>To Date</Label><Input type="date" className="mt-1 w-40" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
        <div>
          <Label>Vehicle Filter</Label>
          <select className="mt-1 h-9 border rounded-md px-2 text-sm bg-white min-w-[140px]" value={vehicleFilter} onChange={e => setVehicleFilter(e.target.value)} data-testid="veh-parts-vehicle-filter">
            <option value="">All Vehicles</option>
            {data.map(v => <option key={v.door_no} value={v.door_no}>{v.door_no}</option>)}
          </select>
        </div>
        <Button onClick={fetch} disabled={loading} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="veh-parts-fetch-btn">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Generate Report
        </Button>
      </div>
      <ReportHeader title="Vehicle-wise Parts Issue Details" subtitle={`${dateFrom} to ${dateTo}`} onRefresh={fetch} loading={loading} onExport={exportXls} onPdf={exportPdf} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <SummaryCard label="Vehicles" value={filteredData.length} />
        <SummaryCard label="Grand Total" value={`₹ ${filteredTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
      </div>
      {filteredData.length === 0 ? (
        <div className="text-center text-slate-400 py-12 border rounded-lg">No data — click Generate Report</div>
      ) : filteredData.map(veh => (
        <div key={veh.door_no} className="border rounded-lg overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-800 text-white hover:bg-slate-700 transition-colors"
            onClick={() => toggle(veh.door_no)}
            data-testid={`veh-row-${veh.door_no}`}
          >
            <span className="font-semibold">{veh.door_no}</span>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-300">{veh.items.length} item(s)</span>
              <span className="font-bold text-emerald-300">₹ {veh.subtotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              <span className="text-slate-400">{expanded[veh.door_no] ? '▲' : '▼'}</span>
            </div>
          </button>
          {expanded[veh.door_no] && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {['SL','Date','Part No','Part Name','Make','Category','UOM','Qty','Unit Price','Amount'].map(h => (
                      <TableHead key={h} className="text-xs py-2 px-3">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {veh.items.map((r, i) => (
                    <TableRow key={i} className="hover:bg-slate-50 text-sm">
                      <TableCell className="px-3 py-1.5 text-slate-500">{i+1}</TableCell>
                      <TableCell className="px-3 py-1.5">{r.date}</TableCell>
                      <TableCell className="px-3 py-1.5 text-slate-600">{r.part_no || '—'}</TableCell>
                      <TableCell className="px-3 py-1.5">{r.part_name}</TableCell>
                      <TableCell className="px-3 py-1.5">{r.make || '—'}</TableCell>
                      <TableCell className="px-3 py-1.5"><Badge variant="outline" className="text-xs">{r.category}</Badge></TableCell>
                      <TableCell className="px-3 py-1.5">{r.uom}</TableCell>
                      <TableCell className="px-3 py-1.5 text-center">{r.qty}</TableCell>
                      <TableCell className="px-3 py-1.5 text-right">₹{fmt(r.unit_price)}</TableCell>
                      <TableCell className="px-3 py-1.5 text-right font-semibold">₹{fmt(r.amount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-amber-50 font-bold">
                    <TableCell colSpan={9} className="px-3 py-2 text-right text-slate-700">Subtotal — {veh.door_no}</TableCell>
                    <TableCell className="px-3 py-2 text-right text-amber-800">₹{fmt(veh.subtotal)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 5. Vehicle-wise Consumption Amount
// ─────────────────────────────────────────────────────────────
function VehicleConsumptionAmountReport() {
  const today = getShiftDate();
  const firstOfMonth = getShiftMonthStart();
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/reports/vehicle-consumption-amount`, {
        headers: authHeaders(),
        params: { date_from: dateFrom, date_to: dateTo }
      });
      setData(res.data.data || []);
      setTotal(res.data.total_amount || 0);
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  const exportXls = () => {
    const rows = data.map(r => ({ 'SL No': r.sl_no, 'Door No': r.door_no, 'Amount (₹)': r.amount }));
    rows.push({ 'SL No': '', 'Door No': 'TOTAL', 'Amount (₹)': total });
    exportToExcel(rows, `Vehicle_Consumption_Amount_${dateFrom}_to_${dateTo}`);
  };

  const exportPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text(`Vehicle-wise Consumption Amount — ${dateFrom} to ${dateTo}`, 14, 15);
    doc.autoTable({
      startY: 22,
      head: [['SL No', 'Door No (Vehicle)', 'Amount (₹)']],
      body: [...data.map(r => [r.sl_no, r.door_no, `₹${r.amount.toLocaleString()}`]), ['', 'TOTAL', `₹${total.toLocaleString()}`]],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 41, 59] },
    });
    doc.save(`Vehicle_Consumption_${dateFrom}_to_${dateTo}.pdf`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end bg-slate-50 rounded-lg p-4">
        <div><Label>From Date</Label><Input type="date" className="mt-1 w-40" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
        <div><Label>To Date</Label><Input type="date" className="mt-1 w-40" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
        <Button onClick={fetch} disabled={loading} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="consumption-fetch-btn">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Generate Report
        </Button>
      </div>
      <ReportHeader title="Vehicle-wise Consumption Amount" subtitle={`${dateFrom} to ${dateTo}`} onRefresh={fetch} loading={loading} onExport={exportXls} onPdf={exportPdf} />
      <div className="grid grid-cols-2 gap-3 mb-4">
        <SummaryCard label="Total Vehicles" value={data.length} />
        <SummaryCard label="Total Consumption" value={`₹ ${total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader className="bg-slate-800">
            <TableRow>
              <TableHead className="text-white text-xs py-2 px-3">SL No</TableHead>
              <TableHead className="text-white text-xs py-2 px-3">Door No (Vehicle)</TableHead>
              <TableHead className="text-white text-xs py-2 px-3 text-right">Amount (₹)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-8">No data — click Generate Report</TableCell></TableRow>
            ) : data.map((r) => (
              <TableRow key={r.door_no} className="hover:bg-slate-50 text-sm">
                <TableCell className="px-3 py-2 text-slate-500">{r.sl_no}</TableCell>
                <TableCell className="px-3 py-2 font-medium">{r.door_no}</TableCell>
                <TableCell className="px-3 py-2 text-right font-semibold">₹{fmt(r.amount)}</TableCell>
              </TableRow>
            ))}
            {data.length > 0 && (
              <TableRow className="bg-slate-100 font-bold">
                <TableCell colSpan={2} className="px-3 py-2 text-right">Total Consumption Amount</TableCell>
                <TableCell className="px-3 py-2 text-right text-slate-800">₹{fmt(total)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 6. Vehicle-wise Costing Report
// ─────────────────────────────────────────────────────────────
const COST_CATEGORIES = ['Spare Parts','Lubricants','Filters','Hoses','Tyres & Tubes','Cutting Edges','Picks & Locks','Tooth Point & Locks','Under Carriage','Other'];

function VehicleCostingReport() {
  const today = getShiftDate();
  const firstOfMonth = getShiftMonthStart();
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState([]);
  const [colTotals, setColTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [vehicleFilter, setVehicleFilter] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/reports/vehicle-costing`, {
        headers: authHeaders(),
        params: { date_from: dateFrom, date_to: dateTo }
      });
      setData(res.data.data || []);
      setColTotals(res.data.column_totals || {});
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  const vehicles = data.map(r => r.door_no);
  const filtered = vehicleFilter ? data.filter(r => r.door_no === vehicleFilter) : data;
  const filteredColTotals = COST_CATEGORIES.reduce((acc, c) => {
    acc[c] = filtered.reduce((s, r) => s + (r[c] || 0), 0);
    return acc;
  }, { total: filtered.reduce((s, r) => s + (r.total || 0), 0) });

  const exportXls = () => {
    const rows = filtered.map(r => {
      const row = { 'SL': r.sl_no, 'Door No': r.door_no };
      COST_CATEGORIES.forEach(c => { row[c] = r[c]; });
      row['Total'] = r.total;
      return row;
    });
    const totRow = { 'SL': '', 'Door No': 'TOTAL' };
    COST_CATEGORIES.forEach(c => { totRow[c] = filteredColTotals[c]; });
    totRow['Total'] = filteredColTotals['total'];
    rows.push(totRow);
    exportToExcel(rows, `Vehicle_Costing_${dateFrom}_to_${dateTo}`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(12);
    doc.text(`Vehicle-wise Costing Report — ${dateFrom} to ${dateTo}${vehicleFilter ? ` | ${vehicleFilter}` : ''}`, 14, 15);
    const head = [['SL', 'Door No', ...COST_CATEGORIES, 'Total']];
    const body = filtered.map(r => [r.sl_no, r.door_no, ...COST_CATEGORIES.map(c => r[c] > 0 ? `₹${r[c]}` : '—'), `₹${r.total}`]);
    doc.autoTable({ startY: 22, head, body, styles: { fontSize: 7 }, headStyles: { fillColor: [30, 41, 59] } });
    doc.save(`Vehicle_Costing_${dateFrom}_to_${dateTo}.pdf`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end bg-slate-50 rounded-lg p-4">
        <div><Label>From Date</Label><Input type="date" className="mt-1 w-40" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
        <div><Label>To Date</Label><Input type="date" className="mt-1 w-40" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
        <div>
          <Label>Vehicle Filter</Label>
          <select className="mt-1 h-9 border rounded-md px-2 text-sm bg-white min-w-[140px]" value={vehicleFilter} onChange={e => setVehicleFilter(e.target.value)} data-testid="costing-vehicle-filter">
            <option value="">All Vehicles</option>
            {vehicles.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <Button onClick={fetch} disabled={loading} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="costing-fetch-btn">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Generate Report
        </Button>
      </div>
      <ReportHeader title="Vehicle-wise Costing Report" subtitle={`${dateFrom} to ${dateTo}`} onRefresh={fetch} loading={loading} onExport={exportXls} onPdf={exportPdf} />
      <p className="text-xs text-slate-500">Categories based on SKU classification in Inventory Management</p>
      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader className="bg-slate-800">
            <TableRow>
              <TableHead className="text-white text-xs py-2 px-2 sticky left-0 bg-slate-800">SL</TableHead>
              <TableHead className="text-white text-xs py-2 px-2 sticky left-8 bg-slate-800">Door No</TableHead>
              {COST_CATEGORIES.map(c => (
                <TableHead key={c} className="text-white text-xs py-2 px-2 text-right whitespace-nowrap">{c}</TableHead>
              ))}
              <TableHead className="text-white text-xs py-2 px-2 text-right font-bold bg-slate-700">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={13} className="text-center text-slate-400 py-8">No data — click Generate Report</TableCell></TableRow>
            ) : filtered.map((r) => (
              <TableRow key={r.door_no} className="hover:bg-slate-50 text-sm">
                <TableCell className="px-2 py-1.5 text-slate-500 sticky left-0 bg-white">{r.sl_no}</TableCell>
                <TableCell className="px-2 py-1.5 font-medium sticky left-8 bg-white">{r.door_no}</TableCell>
                {COST_CATEGORIES.map(c => (
                  <TableCell key={c} className="px-2 py-1.5 text-right text-sm">{r[c] > 0 ? `₹${fmt(r[c])}` : '—'}</TableCell>
                ))}
                <TableCell className="px-2 py-1.5 text-right font-bold text-slate-800">₹{fmt(r.total)}</TableCell>
              </TableRow>
            ))}
            {filtered.length > 0 && (
              <TableRow className="bg-slate-100 font-bold">
                <TableCell colSpan={2} className="px-2 py-2 text-right">TOTAL</TableCell>
                {COST_CATEGORIES.map(c => (
                  <TableCell key={c} className="px-2 py-2 text-right text-xs">₹{fmt(filteredColTotals[c] || 0)}</TableCell>
                ))}
                <TableCell className="px-2 py-2 text-right font-bold">₹{fmt(filteredColTotals['total'] || 0)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 7. Annual Vehicle-wise Consumption
// ─────────────────────────────────────────────────────────────
function AnnualConsumptionReport() {
  const currentFY = new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1;
  const [finYear, setFinYear] = useState(String(currentFY));
  const [fuelRate, setFuelRate] = useState('');
  const [data, setData] = useState([]);
  const [colTotals, setColTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [fyLabel, setFyLabel] = useState('');
  const [showMode, setShowMode] = useState('total'); // 'total' | 'parts' | 'fuel'

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/reports/annual-consumption`, {
        headers: authHeaders(),
        params: { fin_year: parseInt(finYear), fuel_rate: parseFloat(fuelRate) || 0 }
      });
      setData(res.data.data || []);
      setColTotals(res.data.column_totals || {});
      setFyLabel(res.data.fin_year || '');
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  }, [finYear, fuelRate]);

  const exportXls = () => {
    const rows = data.map(r => {
      const row = { 'SL': r.sl_no, 'Door No': r.door_no };
      FY_MONTHS.forEach(m => {
        row[`${m} Parts`] = r[`${m}_parts`];
        row[`${m} Fuel L`] = r[`${m}_fuel_liters`];
        row[`${m} Fuel ₹`] = r[`${m}_fuel_cost`];
        row[`${m} Total`] = r[`${m}_total`];
      });
      row['Total Parts'] = r.total_parts;
      row['Total Fuel (L)'] = r.total_fuel_liters;
      row['Total Fuel Cost'] = r.total_fuel_cost;
      row['Grand Total'] = r.grand_total;
      return row;
    });
    exportToExcel(rows, `Annual_Consumption_FY${finYear}_${parseInt(finYear)+1}`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(11);
    doc.text(`Annual Vehicle-wise Consumption — FY ${finYear}-${parseInt(finYear)+1}`, 14, 15);
    doc.autoTable({
      startY: 22,
      head: [['SL', 'Door No', ...FY_MONTHS, 'Total']],
      body: data.map(r => [r.sl_no, r.door_no, ...FY_MONTHS.map(m => getVal(r,m) > 0 ? (showMode==='fuel' ? `${getVal(r,m)} L` : `₹${getVal(r,m)}`) : '—'), showMode==='fuel' ? `${r.total_fuel_liters} L` : `₹${fmt(showMode==='parts' ? r.total_parts : r.grand_total)}`]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [30, 41, 59] },
    });
    doc.save(`Annual_Consumption_FY${finYear}.pdf`);
  };

  const getVal = (r, m) => {
    if (showMode === 'parts') return r[`${m}_parts`];
    if (showMode === 'fuel') return r[`${m}_fuel_liters`];
    return r[`${m}_total`];
  };
  const getColVal = (m) => {
    if (showMode === 'parts') return colTotals[`${m}_parts`];
    if (showMode === 'fuel') return colTotals[`${m}_fuel_liters`];
    return colTotals[`${m}_total`];
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end bg-slate-50 rounded-lg p-4">
        <div>
          <Label>Financial Year</Label>
          <Select value={finYear} onValueChange={setFinYear}>
            <SelectTrigger className="mt-1 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2023,2024,2025,2026].map(y => <SelectItem key={y} value={String(y)}>FY {y}-{y+1}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Fuel Rate (₹/Liter)</Label>
          <Input type="number" min="0" step="0.01" placeholder="e.g. 95.50" className="mt-1 w-36" value={fuelRate} onChange={e => setFuelRate(e.target.value)} />
        </div>
        <Button onClick={fetch} disabled={loading} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="annual-fetch-btn">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Generate Report
        </Button>
      </div>
      <ReportHeader title="Annual Vehicle-wise Consumption" subtitle={fyLabel} onRefresh={fetch} loading={loading} onExport={exportXls} onPdf={exportPdf} />
      {fuelRate && <p className="text-xs text-blue-600 bg-blue-50 rounded px-3 py-1">Fuel rate: ₹{fuelRate}/L — fuel cost = liters × rate</p>}
      
      <div className="flex gap-2 mb-2">
        <span className="text-xs text-slate-500 self-center">Show:</span>
        {[{k:'total',l:'Total (Parts + Fuel)'},{k:'parts',l:'Parts Only'},{k:'fuel',l:'Fuel Liters'}].map(o => (
          <button key={o.k} onClick={() => setShowMode(o.k)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${showMode === o.k ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-300 text-slate-600 hover:border-slate-500'}`}>
            {o.l}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader className="bg-slate-800">
            <TableRow>
              <TableHead className="text-white text-xs py-2 px-2 sticky left-0 bg-slate-800">SL</TableHead>
              <TableHead className="text-white text-xs py-2 px-2 sticky left-8 bg-slate-800 whitespace-nowrap">Door No</TableHead>
              {FY_MONTHS.map(m => (
                <TableHead key={m} className="text-white text-xs py-2 px-2 text-right">{m}</TableHead>
              ))}
              <TableHead className="text-white text-xs py-2 px-2 text-right bg-slate-700">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={15} className="text-center text-slate-400 py-8">No data — click Generate Report</TableCell></TableRow>
            ) : data.map((r) => (
              <TableRow key={r.door_no} className="hover:bg-slate-50 text-sm">
                <TableCell className="px-2 py-1.5 text-slate-500 sticky left-0 bg-white">{r.sl_no}</TableCell>
                <TableCell className="px-2 py-1.5 font-medium sticky left-8 bg-white">{r.door_no}</TableCell>
                {FY_MONTHS.map(m => {
                  const val = getVal(r, m);
                  return (
                    <TableCell key={m} className="px-2 py-1.5 text-right text-xs">
                      {val > 0 ? (showMode === 'fuel' ? `${val.toLocaleString('en-IN')} L` : `₹${fmt(val)}`) : '—'}
                    </TableCell>
                  );
                })}
                <TableCell className="px-2 py-1.5 text-right font-bold">
                  {showMode === 'fuel' ? `${r.total_fuel_liters?.toLocaleString('en-IN')} L` : `₹${fmt(showMode === 'parts' ? r.total_parts : r.grand_total)}`}
                </TableCell>
              </TableRow>
            ))}
            {data.length > 0 && (
              <TableRow className="bg-slate-100 font-bold">
                <TableCell colSpan={2} className="px-2 py-2 text-right">TOTAL</TableCell>
                {FY_MONTHS.map(m => {
                  const val = getColVal(m) || 0;
                  return (
                    <TableCell key={m} className="px-2 py-2 text-right text-xs">
                      {val > 0 ? (showMode === 'fuel' ? `${val.toLocaleString('en-IN')} L` : `₹${fmt(val)}`) : '—'}
                    </TableCell>
                  );
                })}
                <TableCell className="px-2 py-2 text-right">
                  {showMode === 'fuel' ? `${(colTotals.total_fuel_liters||0).toLocaleString('en-IN')} L` : `₹${fmt(showMode === 'parts' ? colTotals.total_parts : colTotals.grand_total)}`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {data.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
          <SummaryCard label="Total Parts Cost" value={`₹ ${(colTotals.total_parts||0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
          <SummaryCard label="Total Fuel (L)" value={`${(colTotals.total_fuel_liters||0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} L`} />
          {fuelRate && <SummaryCard label="Total Fuel Cost" value={`₹ ${(colTotals.total_fuel_cost||0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />}
          {fuelRate && <SummaryCard label="Grand Total" value={`₹ ${(colTotals.grand_total||0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Import Modal
// ─────────────────────────────────────────────────────────────

const IMPORT_CONFIGS = {
  issued: {
    label: 'Material Issued',
    description: 'Import historical parts issue records',
    endpoint: '/api/reports/import/issued',
    recordKey: 'records',
    templateHeaders: ['date','door_no','part_name','part_no','quantity','unit_price','notes'],
    templateSample: [
      ['2025-01-15','VT-65','Filter Element','FE-001',2,450,'Regular maintenance'],
      ['2025-01-16','DT-12','Engine Oil 15W40','EO-002',4,320,''],
    ],
    columns: [
      { key: 'date', label: 'Date (YYYY-MM-DD)', required: true },
      { key: 'door_no', label: 'Vehicle Door No', required: true },
      { key: 'part_name', label: 'Part Name', required: true },
      { key: 'part_no', label: 'Part No', required: false },
      { key: 'quantity', label: 'Quantity', required: true },
      { key: 'unit_price', label: 'Unit Price (₹)', required: false },
      { key: 'notes', label: 'Notes', required: false },
    ],
    mapRow: (r) => ({
      date: String(r.date || r.Date || '').trim(),
      door_no: String(r.door_no || r['Door No'] || r['Vehicle Door No'] || '').trim(),
      part_name: String(r.part_name || r['Part Name'] || '').trim(),
      part_no: String(r.part_no || r['Part No'] || '').trim(),
      quantity: parseInt(r.quantity || r.Quantity || 0),
      unit_price: parseFloat(r.unit_price || r['Unit Price (₹)'] || 0),
      notes: String(r.notes || r.Notes || '').trim(),
    }),
  },
  received: {
    label: 'Material Received',
    description: 'Import historical purchase/GRN records',
    endpoint: '/api/reports/import/received',
    recordKey: 'records',
    templateHeaders: ['date','supplier','po_no','part_name','part_no','quantity','unit_price','bill_no','grn_no'],
    templateSample: [
      ['2025-01-10','ABC Traders','PO-001','Filter Element','FE-001',10,430,'INV-2025-001','GRN-001'],
      ['2025-01-12','XYZ Parts','PO-002','Engine Oil 15W40','EO-002',20,310,'INV-2025-002','GRN-002'],
    ],
    columns: [
      { key: 'date', label: 'Date (YYYY-MM-DD)', required: true },
      { key: 'supplier', label: 'Supplier Name', required: true },
      { key: 'po_no', label: 'PO No', required: false },
      { key: 'part_name', label: 'Part Name', required: true },
      { key: 'part_no', label: 'Part No', required: false },
      { key: 'quantity', label: 'Quantity', required: true },
      { key: 'unit_price', label: 'Unit Price (₹)', required: true },
      { key: 'bill_no', label: 'Bill / Invoice No', required: false },
      { key: 'grn_no', label: 'GRN No', required: false },
    ],
    mapRow: (r) => ({
      date: String(r.date || r.Date || '').trim(),
      supplier: String(r.supplier || r['Supplier Name'] || r.Supplier || '').trim(),
      po_no: String(r.po_no || r['PO No'] || '').trim(),
      part_name: String(r.part_name || r['Part Name'] || '').trim(),
      part_no: String(r.part_no || r['Part No'] || '').trim(),
      quantity: parseInt(r.quantity || r.Quantity || 0),
      unit_price: parseFloat(r.unit_price || r['Unit Price (₹)'] || 0),
      bill_no: String(r.bill_no || r['Bill / Invoice No'] || '').trim(),
      grn_no: String(r.grn_no || r['GRN No'] || '').trim(),
    }),
  },
  diesel: {
    label: 'Diesel Consumption',
    description: 'Import historical fuel consumption records',
    endpoint: '/api/reports/import/diesel',
    recordKey: 'records',
    templateHeaders: ['date','door_no','fuel_liters','km_reading','hm_reading'],
    templateSample: [
      ['2025-01-15','VT-65',250,15420,2340],
      ['2025-01-16','DT-12',180,45230,0],
    ],
    columns: [
      { key: 'date', label: 'Date (YYYY-MM-DD)', required: true },
      { key: 'door_no', label: 'Vehicle Door No', required: true },
      { key: 'fuel_liters', label: 'Fuel Liters', required: true },
      { key: 'km_reading', label: 'KM Reading', required: false },
      { key: 'hm_reading', label: 'HM Reading', required: false },
    ],
    mapRow: (r) => ({
      date: String(r.date || r.Date || '').trim(),
      door_no: String(r.door_no || r['Vehicle Door No'] || '').trim(),
      fuel_liters: parseFloat(r.fuel_liters || r['Fuel Liters'] || 0),
      km_reading: parseFloat(r.km_reading || r['KM Reading'] || 0),
      hm_reading: parseFloat(r.hm_reading || r['HM Reading'] || 0),
    }),
  },
};

function ImportTab({ config }) {
  const fileRef = useRef();
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState('');

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([config.templateHeaders, ...config.templateSample]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, `${config.label}_Import_Template.xlsx`);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary', cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        const mapped = rows.map(config.mapRow).filter(r => r.date && (r.door_no || r.supplier || r.part_name));
        setPreview(mapped);
        if (mapped.length === 0) toast.error('No valid rows found. Check that headers match the template.');
        else toast.success(`${mapped.length} rows ready to import`);
      } catch (err) {
        toast.error('Failed to parse Excel file');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    if (!preview.length) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}${config.endpoint}`,
        { records: preview },
        { headers: authHeaders() }
      );
      setResult(res.data);
      if (res.data.inserted > 0) toast.success(`${res.data.inserted} records imported successfully`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} rows had errors`);
      setPreview([]);
      setFileName('');
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">{config.description}</p>

      {/* Step 1: Download template */}
      <div className="bg-slate-50 border rounded-lg p-4 flex items-start gap-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700">Step 1 — Download Template</p>
          <p className="text-xs text-slate-500 mt-0.5">Fill in your historical data using the required format</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {config.columns.map(c => (
              <span key={c.key} className={`text-xs px-2 py-0.5 rounded-full border ${c.required ? 'bg-red-50 border-red-200 text-red-700' : 'bg-slate-100 border-slate-200 text-slate-600'}`}>
                {c.label}{c.required ? ' *' : ''}
              </span>
            ))}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={downloadTemplate} className="flex-shrink-0">
          <Download className="h-4 w-4 mr-1.5" /> Template
        </Button>
      </div>

      {/* Step 2: Upload */}
      <div className="bg-slate-50 border rounded-lg p-4">
        <p className="text-sm font-medium text-slate-700 mb-2">Step 2 — Upload Filled Excel</p>
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFile}
            className="hidden"
            id={`file-${config.label}`}
          />
          <label htmlFor={`file-${config.label}`}>
            <Button variant="outline" size="sm" asChild>
              <span className="cursor-pointer"><Upload className="h-4 w-4 mr-1.5" /> Choose File</span>
            </Button>
          </label>
          {fileName && <span className="text-sm text-slate-600 truncate max-w-xs">{fileName}</span>}
          {preview.length > 0 && (
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">{preview.length} rows ready</Badge>
          )}
        </div>
      </div>

      {/* Preview */}
      {preview.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-slate-100 px-4 py-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Preview ({preview.length} rows)</span>
            <Button
              size="sm"
              className="bg-slate-800 hover:bg-slate-700 text-white"
              onClick={handleImport}
              disabled={loading}
              data-testid="confirm-import-btn"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {loading ? 'Importing...' : `Import ${preview.length} Records`}
            </Button>
          </div>
          <div className="overflow-x-auto max-h-64">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs px-3 py-2">#</TableHead>
                  {config.columns.map(c => (
                    <TableHead key={c.key} className="text-xs px-3 py-2">{c.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.slice(0, 10).map((row, i) => (
                  <TableRow key={i} className="text-xs">
                    <TableCell className="px-3 py-1.5 text-slate-400">{i + 1}</TableCell>
                    {config.columns.map(c => (
                      <TableCell key={c.key} className="px-3 py-1.5">{String(row[c.key] ?? '')}</TableCell>
                    ))}
                  </TableRow>
                ))}
                {preview.length > 10 && (
                  <TableRow>
                    <TableCell colSpan={config.columns.length + 1} className="text-center text-slate-400 text-xs py-2">
                      ...and {preview.length - 10} more rows
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`rounded-lg border p-4 ${result.errors?.length ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            {result.errors?.length ? (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            ) : (
              <CheckCircle className="h-4 w-4 text-emerald-600" />
            )}
            <span className="text-sm font-medium">
              {result.inserted} of {result.total} records imported successfully
            </span>
          </div>
          {result.errors?.length > 0 && (
            <div className="space-y-1 mt-2">
              <p className="text-xs text-amber-700 font-medium">Errors:</p>
              {result.errors.slice(0, 5).map((e, i) => (
                <p key={i} className="text-xs text-amber-600">Row {e.row}: {e.error}</p>
              ))}
              {result.errors.length > 5 && (
                <p className="text-xs text-amber-500">...and {result.errors.length - 5} more errors</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ImportDataModal({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-slate-700" />
            Import Historical Data
          </DialogTitle>
          <DialogDescription>
            Upload older data from Excel files to populate reports. Records are marked as imported.
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="issued" className="mt-2">
          <TabsList className="w-full">
            <TabsTrigger value="issued" className="flex-1 text-sm">Material Issued</TabsTrigger>
            <TabsTrigger value="received" className="flex-1 text-sm">Material Received</TabsTrigger>
            <TabsTrigger value="diesel" className="flex-1 text-sm">Diesel</TabsTrigger>
          </TabsList>
          {Object.entries(IMPORT_CONFIGS).map(([key, cfg]) => (
            <TabsContent key={key} value={key} className="mt-4">
              <ImportTab config={cfg} />
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Reports Page
// ─────────────────────────────────────────────────────────────
export default function ReportsPage({ user }) {
  const [importOpen, setImportOpen] = useState(false);

  if (!user?.is_admin && !user?.is_super_admin) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-500">Admin access required to view reports.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-800 rounded-lg">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Reports</h1>
            <p className="text-sm text-slate-500">Generate and export operational reports</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setImportOpen(true)}
          className="flex items-center gap-2 border-slate-300 hover:bg-slate-50"
          data-testid="import-data-btn"
        >
          <Upload className="h-4 w-4" />
          Import Historical Data
        </Button>
      </div>

      <ImportDataModal open={importOpen} onClose={setImportOpen} />

      <Tabs defaultValue="daily-issued" className="space-y-6">
        <div className="overflow-x-auto">
          <TabsList className="bg-slate-100 flex-nowrap inline-flex gap-1 p-1 h-auto">
            {[
              { value: 'daily-issued', label: 'Daily Issued', icon: FileText },
              { value: 'daily-received', label: 'Daily Received', icon: FileText },
              { value: 'opening-stock', label: 'Opening Stock', icon: FileText },
              { value: 'vehicle-parts', label: 'Vehicle Parts Issued', icon: FileText },
              { value: 'vehicle-consumption', label: 'Consumption Amount', icon: BarChart3 },
              { value: 'vehicle-costing', label: 'Vehicle Costing', icon: BarChart3 },
              { value: 'annual-consumption', label: 'Annual Consumption', icon: BarChart3 },
            ].map(({ value, label }) => (
              <TabsTrigger key={value} value={value} className="whitespace-nowrap text-xs px-3 py-2" data-testid={`tab-${value}`}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="daily-issued"><DailyIssuedReport /></TabsContent>
        <TabsContent value="daily-received"><DailyReceivedReport /></TabsContent>
        <TabsContent value="opening-stock"><OpeningStockReport /></TabsContent>
        <TabsContent value="vehicle-parts"><VehiclePartsIssuedReport /></TabsContent>
        <TabsContent value="vehicle-consumption"><VehicleConsumptionAmountReport /></TabsContent>
        <TabsContent value="vehicle-costing"><VehicleCostingReport /></TabsContent>
        <TabsContent value="annual-consumption"><AnnualConsumptionReport /></TabsContent>
      </Tabs>
    </div>
  );
}
